import React from "react";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart, effPrice } from "@/context/CartContext";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function CartDrawer() {
  const { items, productMap, subtotal, updateQty, removeItem, drawerOpen, setDrawerOpen } = useCart();

  if (!drawerOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={() => setDrawerOpen(false)}
        data-testid="cart-drawer-backdrop"
      />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-neutral-900 border-l border-neutral-800 flex flex-col animate-reveal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-800">
          <h2 className="font-disp text-2xl uppercase text-white">Your Cart</h2>
          <button
            data-testid="cart-close-button"
            onClick={() => setDrawerOpen(false)}
            className="p-2 text-neutral-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <p data-testid="cart-empty-message" className="text-sm text-neutral-400 font-mono uppercase tracking-widest pt-8 text-center">
              Cart is empty — add some hardware
            </p>
          )}
          {items.map((item) => {
            const p = productMap[item.product_id];
            if (!p) return null;
            return (
              <div
                key={item.product_id}
                data-testid={`cart-item-${item.product_id}`}
                className="flex gap-4 bg-neutral-950 border border-neutral-800 rounded p-3"
              >
                <img src={p.image} alt={p.name} className="w-20 h-20 object-cover rounded border border-neutral-800" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-white leading-tight">{p.name}</h3>
                    <button
                      data-testid={`cart-remove-${item.product_id}`}
                      onClick={() => removeItem(item.product_id)}
                      className="text-neutral-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-xs font-mono text-white mt-1">{fmt(effPrice(p))}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      data-testid={`cart-qty-minus-${item.product_id}`}
                      onClick={() => updateQty(item.product_id, item.quantity - 1)}
                      className="w-6 h-6 flex items-center justify-center bg-neutral-800 hover:bg-neutral-700 rounded text-white"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span data-testid={`cart-qty-${item.product_id}`} className="text-sm font-mono text-white w-6 text-center">
                      {item.quantity}
                    </span>
                    <button
                      data-testid={`cart-qty-plus-${item.product_id}`}
                      onClick={() => updateQty(item.product_id, item.quantity + 1)}
                      className="w-6 h-6 flex items-center justify-center bg-neutral-800 hover:bg-neutral-700 rounded text-white"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-neutral-800 px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-neutral-400">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-disp text-2xl text-white">{fmt(subtotal)}</span>
          </div>
          <button
            data-testid="cart-checkout-button"
            disabled={items.length === 0}
            onClick={() => {
              setDrawerOpen(false);
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full py-3.5 bg-white hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed text-black font-disp text-lg uppercase tracking-wider rounded transition-transform active:scale-[0.99]"
          >
            Proceed to Checkout
          </button>
        </div>
      </aside>
    </div>
  );
}
